// src/utils/roleGates.js
// Role-Based Access Control (RBAC) for Epiphany MD
// Gates fail-open: if userRole is undefined, all permissions return true
// so existing users are never locked out during role migration.

import { useApp } from '../context/AppContext';

export const ROLES = {
  ADMIN:      'admin',
  PROVIDER:   'provider',
  MA:         'ma',
  FRONT_DESK: 'front_desk',
  BILLING:    'billing',
};

export const PERMISSIONS = {
  // Clinical
  signNotes:       ['admin', 'provider'],
  cosignNote:      ['admin', 'provider'],
  createNotes:     ['admin', 'provider', 'ma'],
  viewNotes:       ['admin', 'provider', 'ma'],
  prescribe:       ['admin', 'provider'],
  viewMedications: ['admin', 'provider', 'ma'],

  // Scheduling
  viewSchedule:    ['admin', 'provider', 'ma', 'front_desk'],
  editSchedule:    ['admin', 'provider', 'ma', 'front_desk'],
  checkInPatients: ['admin', 'provider', 'ma', 'front_desk'],

  // Billing
  viewBilling:     ['admin', 'billing'],
  editBilling:     ['admin', 'billing'],
  viewARAnalytics: ['admin', 'billing'],
  viewCoding:      ['admin', 'provider', 'billing'],

  // Admin
  manageUsers:      ['admin'],
  viewAuditLog:     ['admin'],
  manageSettings:   ['admin', 'provider'],
  viewSubscription: ['admin'],

  // Patient data
  viewPatientDemographics: ['admin', 'provider', 'ma', 'front_desk', 'billing'],
  editPatientDemographics: ['admin', 'provider', 'ma', 'front_desk'],
  viewClinicalChart:       ['admin', 'provider', 'ma'],
};

// Pure function — safe to call outside React components
export const hasPermission = (userRole, permission) => {
  if (process.env.REACT_APP_DEMO_MODE === 'true') return true; // demo — show all features to prospects
  if (!userRole) return true; // fail-open when role is undefined
  const allowed = PERMISSIONS[permission];
  if (!allowed) return true; // unknown permission → allow
  return allowed.includes(userRole);
};

// React hook version
export const usePermission = (permission) => {
  const ctx = useApp();
  return hasPermission(ctx?.userRole, permission);
};

// ── Concept 1: System Role (access control) ──────────────────────────────────
// These are the canonical role definitions. Value stored in providers.role /
// staff.role in the database. Controls all permission gates.
export const SYSTEM_ROLES = [
  { value: 'provider',   short: 'Provider',   label: 'Provider (Physician/NP/PA)',          desc: 'Full clinical access, can sign notes',        color: 'var(--teal)' },
  { value: 'admin',      short: 'Admin',      label: 'Admin — full system access',            desc: 'Full access including billing & settings',    color: '#ef4444' },
  { value: 'ma',         short: 'MA',         label: 'Medical Assistant — clinical support',  desc: 'Clinical support, cannot sign notes',         color: '#8b5cf6' },
  { value: 'front_desk', short: 'Front Desk', label: 'Front Desk — scheduling only',          desc: 'Scheduling and demographics only',            color: '#f59e0b' },
  { value: 'billing',    short: 'Billing',    label: 'Billing — billing and coding only',     desc: 'Billing and coding access only',              color: '#3b82f6' },
];

// ── Concept 2: Job Title (display only) ──────────────────────────────────────
// Stored in providers.job_title / staff.job_title. Does NOT affect permissions.
export const JOB_TITLES = [
  "Physician (MD/DO)",
  "Nurse Practitioner (NP)",
  "Physician Associate (PA)",
  "Registered Nurse (RN)",
  "Medical Assistant (MA)",
  "Licensed Practical Nurse (LPN)",
  "Care Coordinator",
  "Front Desk / Receptionist",
  "Billing Specialist",
  "Practice Manager",
  "Administrator",
  "X-Ray Technician",
  "Surgical Technician",
  "Other",
];

// Convenience lookups derived from SYSTEM_ROLES
export const ROLE_LABELS = Object.fromEntries(SYSTEM_ROLES.map(r => [r.value, r.short]));
export const ROLE_COLORS = Object.fromEntries(SYSTEM_ROLES.map(r => [r.value, r.color]));
export const ROLE_DESCS  = Object.fromEntries(SYSTEM_ROLES.map(r => [r.value, r.desc]));
